/**
 * AccountService - Servis za upravljanje Account API pozivima
 * 
 * Omogućava komunikaciju sa Account API endpoint-ima:
 * - GET /profile-info - Učitavanje podataka profila
 * - GET /login - Provera statusa prijave
 * - POST /profile-info/save-or-update - Čuvanje ili ažuriranje profila
 */

import ApiService from '@/core/api/ApiService';
import { clearLoginDataCache, getCachedLoginData, setCachedLoginData } from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import authDebug from '../utils/authDebug';

/** Jedan in-flight checkLogin zahtev – svi konkurentni pozivi dele isti rezultat (izbegava N poziva /login pri refresh-u). */
let checkLoginInFlight = null;

class AccountService {
  /**
   * Učitava podatke korisničkog profila
   * @returns {Promise<Object>} Objekat sa podacima profila
   * @throws {Error} Ako API poziv ne uspe
   * 
   * @example
   * const profile = await AccountService.getProfileInfo();
   * // profile = { email: '...', first_name: '...', last_name: '...', ... }
   */
  async getProfileInfo() {
    try {
      const response = await ApiService.get(API_ENDPOINTS.ACCOUNT.PROFILE_INFO_GET);
      
      // API vraća podatke u response.data
      if (response.data) {
        return response.data;
      }
      
      // Ako response.data ne postoji, vrati prazan objekat
      return {};
    } catch (error) {
      // Ako je 401, korisnik nije prijavljen
      if (error.response && error.response.status === 401) {
        throw new Error('Korisnik nije prijavljen');
      }
      
      // Ako je 404, profil ne postoji
      if (error.response && error.response.status === 404) {
        throw new Error('Profil nije pronađen');
      }
      
      // Za ostale greške, baci originalnu grešku
      throw error;
    }
  }

  /**
   * @private
   * @param {object} data
   * @returns {{ profile: object, roles: unknown[], organizations: object }}
   */
  _normalizeLoginResponse(data = {}) {
    // BE može da vrati UUID naloga na top-levelu (data.id / data.account_id) umesto unutar data.profile.
    // Spajamo ih u profile.id kako bi resolveProposedByUuid mogao da ih pronađe.
    const topLevelId = data.id || data.account_id || data.accountId || data.user_id || null;
    const rawProfile = data.profile || {};
    const profile = topLevelId && !rawProfile.id
      ? { id: topLevelId, ...rawProfile }
      : rawProfile;

    if (process.env.NODE_ENV !== 'production' && !profile.id) {
      console.warn('[AccountService] /login profile ne sadrži id/UUID — proposed_by neće biti poslat. Raw response:', data);
    }

    return {
      profile,
      roles: data.roles || [],
      organizations: data.organizations || { requested: [], verified: [] },
    };
  }

  /**
   * @private
   * @param {string|null|undefined} token
   * @param {object} data
   */
  _cacheLoginResponse(token, data) {
    if (!token || !data) return;
    setCachedLoginData(token, this._normalizeLoginResponse(data));
  }

  /**
   * Proverava status prijave korisnika. Prvo koristi keš (bez /login poziva), zatim in-flight deduplikaciju.
   * @param {AbortSignal|null} signal
   * @param {{ forceRefresh?: boolean }} [options] — forceRefresh: novi GET /login (npr. posle request-access)
   * @returns {Promise<Object>} Objekat sa statusom prijave (profile, roles, organizations)
   * @throws {Error} Ako API poziv ne uspe
   */
  async checkLogin(signal = null, options = {}) {
    const { forceRefresh = false } = options;
    const AuthService = (await import('@/core/auth/AuthService')).default;
    const user = await AuthService.getUser();
    const token = user?.access_token;

    if (forceRefresh) {
      clearLoginDataCache();
    } else {
      const cached = token ? getCachedLoginData(token) : null;
      if (cached) {
        return this._normalizeLoginResponse(cached);
      }
    }

    if (checkLoginInFlight && !forceRefresh) {
      return checkLoginInFlight;
    }

    // Namerno ignorišemo signal pozivaoca za shared in-flight request.
    // Ako bi signal bio vezan za HTTP request, jedan pozivalac koji abort-uje (npr. useAuthRedirect
    // timeout) ubio bi i sve ostale pozivaoce koji čekaju isti promise (AuthContext, _ensureLoginData).
    // Pozivalac koji želi timeout treba da implementira Promise.race na svom nivou (useAuthRedirect to radi).
    const promise = this._doCheckLogin(null).then((data) => {
      const normalized = this._normalizeLoginResponse(data);
      this._cacheLoginResponse(token, data);
      return normalized;
    });
    checkLoginInFlight = promise;
    promise.finally(() => {
      checkLoginInFlight = null;
    }).catch(() => {}); // suppress unhandled rejection on the finally-derived promise
    return promise;
  }

  /**
   * Interni jedinstveni poziv ka /login endpointu.
   * @private
   */
  async _doCheckLogin(signal = null) {
    authDebug.step('Login provera započeta');
    
    try {
      const AuthService = (await import('@/core/auth/AuthService')).default;
      const user = await AuthService.getUser();
      
      authDebug.info('Korisnik status', {
        hasUser: !!user,
        hasToken: !!(user?.access_token),
        expiresIn: user?.expires_in ? `${user.expires_in}s` : 'N/A',
      });
      
      const response = await ApiService.get(API_ENDPOINTS.ACCOUNT.LOGIN, {}, signal);
      
      authDebug.success('Login uspešan', {
        status: response.status,
        hasData: !!response.data,
      });
      
      if (response.data) {
        return response.data;
      }
      
      return {};
    } catch (error) {
      authDebug.error('Login greška', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url ? `${error.config.baseURL}${error.config.url}` : 'N/A',
        tokenSent: error.config?.headers?.Authorization ? 'DA' : 'NE',
      });

      // SessionExpiredError znači da je token renewal pao tokom /login poziva.
      // Ovo je "prijava nije uspela" scenario (naročito u OIDC callback-u sa svežim tokenom) —
      // ne propagujemo originalnu poruku "Sesija je istekla" koja zbunjuje korisnika.
      if (error.name === 'SessionExpiredError') {
        throw new Error('Пријава није успела. Молимо покушајте поново.');
      }

      if (error.response && error.response.status === 401) {
        throw new Error('Korisnik nije prijavljen');
      }
      
      throw error;
    }
  }

  /**
   * Čuva ili ažurira korisnički profil
   * @param {Object} profileData - Podaci profila za čuvanje
   * @param {string} profileData.email - Email adresa
   * @param {string} profileData.first_name - Ime
   * @param {string} profileData.last_name - Prezime
   * @param {string} profileData.country - Država
   * @param {string} profileData.city - Grad
   * @param {string} profileData.postal - Poštanski broj
   * @param {string} profileData.street - Ulica i broj
   * @returns {Promise<Object>} Objekat sa sačuvanim podacima profila
   * @throws {Error} Ako API poziv ne uspe
   * 
   * @example
   * const savedProfile = await AccountService.saveOrUpdateProfile({
   *   email: 'user@example.com',
   *   first_name: 'Petar',
   *   last_name: 'Petrović',
   *   country: 'Srbija',
   *   city: 'Beograd',
   *   postal: '11000',
   *   street: 'Knez Mihailova 1'
   * });
   */
  async saveOrUpdateProfile(profileData) {
    try {
      // Validacija obaveznih polja
      if (!profileData.email) {
        throw new Error('Email adresa je obavezna');
      }

      // Pripremi podatke za slanje
      const dataToSend = {
        email: profileData.email || '',
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        country: profileData.country || '',
        city: profileData.city || '',
        postal: profileData.postal || '',
        street: profileData.street || '',
      };

      const response = await ApiService.post(
        API_ENDPOINTS.ACCOUNT.PROFILE_INFO_SAVE_OR_UPDATE,
        dataToSend
      );

      // API vraća podatke u response.data
      if (response.data) {
        return response.data;
      }

      return dataToSend; // Vrati podatke koje smo poslali ako API ne vraća odgovor
    } catch (error) {
      // Ako je 401, korisnik nije prijavljen
      if (error.response && error.response.status === 401) {
        throw new Error('Korisnik nije prijavljen');
      }

      // Ako je 400, validaciona greška
      if (error.response && error.response.status === 400) {
        const errorMessage = error.response.data?.message || 'Neispravni podaci profila';
        throw new Error(errorMessage);
      }

      // Za ostale greške, baci originalnu grešku
      throw error;
    }
  }
}

// Singleton pattern - kreiranje jedne instance
const accountServiceInstance = new AccountService();
export default accountServiceInstance;

